import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../axiosBaseQuery";
import {
    AuthUserType,
    DownloadOptionsResponse,
    UserType,
    VoicePrint,
} from "@callveriq/type/User";
import { getUserName } from "@callveriq/utils/helper/common.helper";
import { StaticDashboardFiltersType } from "@callveriq/type/Filters.model";

export type RestrictedUserType = UserType & { team: number; role: number };

export type PaginatedUsersResponse = {
    count: number;
    next: string | null;
    previous: string | null;
    results: RestrictedUserType[];
};

export type PaginatedUsersArgs = Partial<{
    search: string;
    page: number;
    page_size: number;
    teams: number[];
    is_restricted: boolean;
    is_internal: boolean;
    agent_activity: StaticDashboardFiltersType["agent_activity"];
    tenure_range: { gte: number; lte: number };
}>;

/**
 * `list_reps` is always paginated now, so every caller must unwrap `results`.
 * A plain array is still accepted so an older backend keeps working.
 */
const unwrapUsers = <T>(response: T[] | { results: T[] } | undefined): T[] =>
    Array.isArray(response) ? response : response?.results ?? [];

export const usersApiSlice = createApi({
    reducerPath: "usersApiSlice",
    baseQuery: axiosBaseQuery({
        transformResponse: (response) => response,
    }),
    tagTypes: ["Voice Print"],
    endpoints: (builder) => ({
        getUserInfo: builder.query<AuthUserType, void>({
            query: () => "/person/person/me/",
        }),
        getUsers: builder.query<UserType[], void>({
            query: () => ({
                url: "person/team/list_reps/",
                method: "POST",
                body: {
                    is_restricted: false,
                },
            }),
            transformResponse: (
                response: UserType[] | { results: UserType[] }
            ) =>
                unwrapUsers(response).map((e) => ({
                    ...e,
                    label: `${getUserName(e)} (${e.email})`,
                })),
        }),
        getInternalUsers: builder.query<UserType[], void>({
            query: () => ({
                url: "person/team/list_reps/",
                method: "POST",
                body: {
                    is_internal: true,
                },
            }),
            transformResponse: (
                response: UserType[] | { results: UserType[] }
            ) =>
                unwrapUsers(response).map((e) => ({
                    ...e,
                    label: `${getUserName(e)} (${e.email})`,
                })),
        }),
        getCoachingApplicableUsers: builder.query<
            UserType[],
            "receive_coaching" | "assign_coaching"
        >({
            query: (permission) => ({
                url: "coachings/users/eligible/",
                method: "GET",
                params: { permission },
            }),
            transformResponse: (
                response: UserType[] | { results: UserType[] }
            ) =>
                unwrapUsers(response).map((e) => ({
                    ...e,
                    label: `${getUserName(e)} (${e.email})`,
                })),
        }),
        getRestrictedUsers: builder.query<
            Array<UserType & { team: number; role: number }>,
            Partial<{
                teams: number[];
                is_restricted: boolean;
                agent_activity?: StaticDashboardFiltersType["agent_activity"];
                tenure_range?: { gte: number; lte: number };
            }> | void
        >({
            query: (payload = {}) => ({
                url: "person/team/list_reps/",
                method: "POST",
                body: { teams: [], is_restricted: true, ...payload },
            }),
            transformResponse: (
                response:
                    | Array<UserType & { team: number; role: number }>
                    | {
                          results: Array<
                              UserType & { team: number; role: number }
                          >;
                      }
            ) =>
                unwrapUsers(response).map((e) => ({
                    ...e,
                    label: `${getUserName(e)} (${e.email})`,
                })),
        }),

        /**
         * Paginated + server-side searched reps.
         * The backend only paginates when `page`/`page_size` is sent, so the
         * unpaginated endpoints above keep returning a flat array.
         */
        getRestrictedUsersPaginated: builder.query<
            PaginatedUsersResponse,
            PaginatedUsersArgs
        >({
            query: ({ search, page = 1, page_size = 25, ...payload }) => ({
                url: "person/team/list_reps/",
                method: "POST",
                body: {
                    teams: [],
                    is_restricted: true,
                    page,
                    page_size,
                    ...(search ? { search } : {}),
                    ...payload,
                },
            }),
            // one cache entry per filter-set; pages are concatenated into it
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const { page: _page, ...rest } = queryArgs;
                return `${endpointName}-${JSON.stringify(rest)}`;
            },
            merge: (currentCache, newItems, { arg }) => {
                if ((arg.page ?? 1) <= 1) return newItems;
                return {
                    ...newItems,
                    results: [...currentCache.results, ...newItems.results],
                };
            },
            forceRefetch: ({ currentArg, previousArg }) =>
                currentArg?.page !== previousArg?.page,
            transformResponse: (
                response: PaginatedUsersResponse | RestrictedUserType[]
            ): PaginatedUsersResponse => {
                const rows = unwrapUsers(response);
                const envelope = Array.isArray(response)
                    ? { count: rows.length, next: null, previous: null }
                    : response;
                return {
                    ...envelope,
                    results: rows.map((e) => ({
                        ...e,
                        label: `${getUserName(e)} (${e.email})`,
                    })),
                };
            },
        }),
        /**
         * Resolve specific reps by id - needed to label selections that are
         * not on the currently loaded page.
         */
        /**
         * Resolve specific users by id. Replaces "fetch every user and .find()"
         * in components that only need a handful of names.
         */
        getUsersByIds: builder.query<
            RestrictedUserType[],
            | number[]
            | {
                  ids: number[];
                  is_internal?: boolean;
                  is_restricted?: boolean;
              }
        >({
            query: (arg) => {
                const {
                    ids,
                    is_internal,
                    is_restricted = true,
                } = Array.isArray(arg) ? { ids: arg } : arg;
                return {
                    url: "person/team/list_reps/",
                    method: "POST",
                    body: {
                        teams: [],
                        is_restricted,
                        ids,
                        ...(is_internal ? { is_internal: true } : {}),
                    },
                };
            },
            transformResponse: (
                response:
                    | RestrictedUserType[]
                    | { results: RestrictedUserType[] }
            ) =>
                unwrapUsers(response).map((e) => ({
                    ...e,
                    label: `${getUserName(e)} (${e.email})`,
                })),
        }),

        getAllAuditors: builder.query<UserType[], void>({
            query: () => "/person/list_auditor/",
            transformResponse: (response: UserType[]) => {
                return Array.isArray(response)
                    ? response?.map((e) => ({ ...e, label: getUserName(e) }))
                    : response;
            },
        }),
        getVoicePrint: builder.query<VoicePrint, void>({
            query: () => "/person/persons/voice_print/",
            providesTags: ["Voice Print"],
        }),
        uploadVoicePrint: builder.mutation<void, FormData>({
            query: (payload) => ({
                url: "/person/persons/voice_print/",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["Voice Print"],
        }),
        createQmsUser: builder.mutation<
            UserType,
            {
                owner_email: string;
                owner_first_name: string;
                owner_last_name: string;
                owner_primary_phone: string;
                team_id: number;
            }
        >({
            query: (payload) => ({
                url: "/person/qms/",
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        usersApiSlice.util.updateQueryData(
                            "getUsers",
                            undefined,
                            (draft) => {
                                Object.assign(draft, [data, ...draft]);
                            }
                        )
                    );
                } catch {}
            },
        }),
        getDownloadOptions: builder.query<DownloadOptionsResponse, void>({
            query: () => ({
                url: "/app/get_live_assist_app_links",
                method: "GET",
                keepUnusedDataFor: 3600,
            }),
        }),
    }),
});

export const {
    useGetUsersQuery,
    useGetRestrictedUsersQuery,
    useGetRestrictedUsersPaginatedQuery,
    useLazyGetRestrictedUsersPaginatedQuery,
    useGetUsersByIdsQuery,
    useLazyGetRestrictedUsersQuery,
    useGetInternalUsersQuery,
    useGetAllAuditorsQuery,
    useGetUserInfoQuery,
    useGetVoicePrintQuery,
    useLazyGetVoicePrintQuery,
    useUploadVoicePrintMutation,
    useCreateQmsUserMutation,
    useGetCoachingApplicableUsersQuery,
    useGetDownloadOptionsQuery,
} = usersApiSlice;
